import React, { useCallback, useEffect, useState } from "react";
import { Form, Grid, Message, Transition } from "semantic-ui-react";
import { createErrorResponse, getDropdownOptions, handleAddressValue, handleFormChange, isValidPermission } from "../Helpers";
import { getMockProfileMetadata } from "../api/mock/MockBrokerApi";
import { getProfilenMetadata } from "../api/BrokerApi";
import "../styles/profile.scss";
import "../styles/main.scss";
import BCFormInput from "./ui/BCFormInput";
import { GlobalConstants, Personas } from "./constants/GlobalConstants";
import BCValidationMessage from "./ui/BCValidationMessage";
import { FeatureFlags } from "./constants/LocalServerConstants";
import { getIsWhiteLabeled } from "../helpers/PermissionHelper";

const BCProfileInfo = ({ ...props }) => {
  const [loanOfficers, setLoanOfficers] = useState([] as any[]);
  const [entitySubTypes, setEntitySubTypes] = useState([] as any[]);
  const validationMessage = (prop) => {
    return (
      <Transition
        animation={props.invalidProps.length === 0 ? "scale" : "shake"}
        duration={{hide: 10, show: 500}}
        visible={props.invalidProps.includes(prop)}
      >
        {props.invalidProps.includes(prop) ? <Message className="gmr-message" negative color="red">This field is required.</Message> : <div/>}
      </Transition>
    );
  };
  const getApplicationMetadataAsync =  useCallback(async () => {
    try {
      const response = FeatureFlags.IS_MOCK ? await getMockProfileMetadata() : await getProfilenMetadata(props.proxyUser.id);
      if (response && response.ok) {
        const profileMetadata = await response.json();
        if (profileMetadata.loanOfficers) {
          const nullOfficerObj = {
            id: null,
            name: "I'm not working with a loan officer"
          };
          profileMetadata.loanOfficers.push(nullOfficerObj);
          setLoanOfficers(profileMetadata.loanOfficers && profileMetadata.loanOfficers);
        }
        setEntitySubTypes(profileMetadata.entitySubTypes);
      }
    }
    catch (error: any) {
      createErrorResponse("Error", "Caught Error while trying to get application metadata", undefined , "borrowerapplication/metadata", undefined, error.status, "GET", error);
    }
  }, [props.proxyUser.id]);

  useEffect(() => {
    getApplicationMetadataAsync();
  }, [getApplicationMetadataAsync]);

  return (
    <Form className="primary-form">
      <Grid className="profile-info-grid">
        <Grid.Row columns={2}>
          <Grid.Column width={16}>
            <BCFormInput
              labelClass="gmr-background-label"
              type="input"
              label="First Name:"
              placeholder="John"
              obj={props.profile}
              field="firstName"
              inputType="text"
              handleFormChange={(value, field) => handleFormChange(value, field, props.profile, props.setProfile)}
              required
              disabled={props.profile.isExternalUser}
            />
            {validationMessage("firstName")}
          </Grid.Column>
          <Grid.Column width={16}>
            <BCFormInput
              labelClass="gmr-background-label"
              type="input"
              label="Last Name:"
              placeholder="Smith"
              obj={props.profile}
              field="lastName"
              inputType="text"
              handleFormChange={(value, field) => handleFormChange(value, field, props.profile, props.setProfile)}
              required
              disabled={props.profile.isExternalUser}
            />
            {validationMessage("lastName")}
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={16}>
            <BCFormInput
              labelClass="gmr-background-label"
              type="input"
              label="Email: "
              placeholder="email@address.com"
              obj={props.profile}
              field="email"
              inputType="text"
              disabled
            />
            {validationMessage("email")}
          </Grid.Column>
          <Grid.Column width={16}>
            <BCFormInput
              labelClass="gmr-background-label"
              type="input"
              label="Mobile Phone: "
              placeholder="(206) 555-1234"
              obj={props.profile}
              field="phone"
              fieldType="phone"
              inputType="text"
              handleFormChange={(value, field) => handleFormChange(value, field, props.profile, props.setProfile)}
              disabled={(!props.profile.isPhoneEditable) || props.profile.isExternalUser}
            />
            {validationMessage("phone")}
          </Grid.Column>
        </Grid.Row>
        {((props.persona === Personas.UNKNOWN && !getIsWhiteLabeled()) || ([Personas.BROKER, Personas.NWL_GUARANTOR, Personas.WL_GUARANTOR].indexOf(props.persona) > -1)) && <>
          <Grid.Row columns={2}>
            <Grid.Column width={16}>
              <BCFormInput
                labelClass="gmr-background-label"
                type="input"
                label="Company:"
                placeholder="Construction Loan Services II"
                obj={props.profile}
                field="company"
                inputType="text"
                value={props.profile.company ? props.profile.company.replace(/\[\d+\]$/, "") : ""}
                handleFormChange={(value, field) => handleFormChange(value, field, props.profile, props.setProfile, GlobalConstants.InputTypes.ORGANIZATION, undefined, 50)}
                required
                disabled={props.profile && props.profile.organizationId}
              />
              {validationMessage("company")}
            </Grid.Column>
            <Grid.Column width={16}>
              <BCFormInput 
                labelClass="gmr-background-label"
                type="dropdown"
                label="Company Type:"
                placeholder="Please Select"
                obj={props.profile}
                field="entitySubTypeId"
                dropdownOptions={getDropdownOptions(entitySubTypes, ["id"], "name")}
                handleFormChange={(value, field) => handleFormChange(value, field, props.profile, props.setProfile)}
                required
                disabled={props.profile && props.profile.organizationId}
              />
              {validationMessage("entitySubTypeId")}
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={16}>
              <BCFormInput
                label="Address:"
                labelClass="gmr-background-label"
                type={GlobalConstants.FormInputTypes.PLACES}
                placeDetailsObject={props.profile}
                setPlaceDetailsObject={props.setProfile}
                placeholder="1234 Main Street, Seattle, WA 98052, USA"
                defaultValue={handleAddressValue({
                  address: props.profile.address,
                  city: props.profile.city,
                  state: props.profile.state,
                  zip: props.profile.zip
                })}
                placeFields={["address", "city", "state", "county", "zip"]}
                required
              />
              {props.invalidProps.includes("city") ? <BCValidationMessage 
                invalidProps={props.invalidProps} 
                prop="city" 
                propText="This city is invalid." 
              /> : props.invalidProps.includes("state") ? <BCValidationMessage 
                invalidProps={props.invalidProps} 
                prop="state"
                propText="This state is invalid."
              /> : props.invalidProps.includes("zip") ? <BCValidationMessage 
                invalidProps={props.invalidProps} 
                prop="zip"
                propText="This zip code is invalid."
              /> : <BCValidationMessage 
                invalidProps={props.invalidProps} 
                prop="address" 
                propText="Please enter a street address." />
              }
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            {(isValidPermission(props.permissions, "BrokerBorrowerApplication", "read") && !getIsWhiteLabeled()) && <Grid.Column width={16}>
              <Grid.Column width={16}>
                <BCFormInput 
                  labelClass="gmr-background-label"
                  type="dropdown"
                  label="Select your loan officer:"
                  placeholder="None"
                  obj={props.profile}
                  field="preferredLoanOfficerOrganizationContactId"
                  dropdownOptions={getDropdownOptions(loanOfficers, ["id"], "name")}
                  handleFormChange={(value, field) => handleFormChange(value, field, props.profile, props.setProfile)}
                  required
                />
                {validationMessage("preferredLoanOfficerOrganizationContactId")}
            </Grid.Column>
            </Grid.Column>} 
          </Grid.Row>
        </>}
      </Grid>
    </Form>
  );
};

export default BCProfileInfo;